import React, { useState, useRef } from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import { addUsage } from './add-usage.js';

const HeicConverter = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [files, setFiles] = useState([]);
  const [outputFormat, setOutputFormat] = useState('png');
  const [quality, setQuality] = useState(92);
  const [submitValue, setSubmitValue] = useState('Convert & Download');
  const [notify, setNotify] = useState('');
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState('');
  const [done, setDone] = useState(0);
  const fileInputRef = useRef(null);

  const toggleModal = () => {
    setVisible(!visible);
    resetState();
  };

  const resetState = () => {
    setFiles([]);
    setOutputFormat('png');
    setQuality(92);
    setSubmitValue('Convert & Download');
    setNotify('');
    setError(null);
    setProcessing(false);
    setProgress('');
    setDone(0);
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const isHeic = (file) =>
    /\.(heic|heif)$/i.test(file.name) ||
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    file.type === '';

  const handleFileChange = (event) => {
    const selected = Array.from(event.target.files || []);
    if (selected.length === 0) return;

    const heicFiles = selected.filter(isHeic);
    if (heicFiles.length !== selected.length) {
      setNotify('Only HEIC/HEIF images are allowed');
      setError('error');
      return;
    }

    setFiles(heicFiles);
    setNotify('');
    setError(null);
    setDone(0);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const convert = async () => {
    if (files.length === 0) return;
    setProcessing(true);
    setSubmitValue(<Spinner />);
    setError(null);
    setNotify('');
    setDone(0);

    try {
      // heic2any pulls in a large WASM decoder, so load it on demand.
      const heic2any = (await import('heic2any')).default;
      const mime = outputFormat === 'png' ? 'image/png' : 'image/jpeg';
      let completed = 0;

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProgress(`Converting ${i + 1} of ${files.length}: ${file.name}`);

        // PNG ignores the quality arg; JPEG uses 0-1. Quality is preserved
        // (no downscaling) — only JPEG compression trades size for fidelity.
        const converted = await heic2any({
          blob: file,
          toType: mime,
          quality: outputFormat === 'png' ? undefined : quality / 100,
        });

        // heic2any returns a Blob (or an array of Blobs for multi-image HEICs).
        const blobs = Array.isArray(converted) ? converted : [converted];
        const base = file.name.replace(/\.(heic|heif)$/i, '');
        blobs.forEach((blob, idx) => {
          const suffix = blobs.length > 1 ? `_${idx + 1}` : '';
          downloadBlob(blob, `${base}${suffix}.${outputFormat === 'png' ? 'png' : 'jpg'}`);
        });

        completed += 1;
        setDone(completed);
      }

      setProgress('');
      setSubmitValue(`Converted ${completed}`);
      addUsage('AWESOME PDF TOOLS HeicConverter', '1.0', completed, window.location.href);
      setTimeout(() => {
        setVisible(false);
        resetState();
      }, 1500);
    } catch (err) {
      console.error('HEIC conversion failed:', err);
      setNotify('Conversion failed. The file may be corrupted or not a valid HEIC image.');
      setError('error');
      setSubmitValue('Convert & Download');
      setProgress('');
      setProcessing(false);
    }
  };

  const totalSize = files.reduce((sum, f) => sum + f.size, 0);

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#FF9900">
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
          </svg>
        </div>
        <h3 className="service-card-title">HEIC Converter</h3>
        <p className="service-card-desc">Convert HEIC photos to PNG or JPEG with quality preserved</p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              <Button variant="primary" disabled={files.length === 0 || processing || error} onClick={convert}>
                {submitValue}
              </Button>
            </SpaceBetween>
          </Box>
        }
        header="HEIC to PNG / JPEG"
      >
        <div>
          <div className="bordered-box">
            <h6 className="container-header">📸 Choose HEIC Images</h6>
            <FormField label="Upload one or more HEIC/HEIF photos" errorText={notify}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".heic,.heif,image/heic,image/heif"
                multiple
                onChange={handleFileChange}
              />
            </FormField>
            {files.length > 0 && (
              <div style={{ marginTop: '10px', fontSize: '13px', color: '#565959' }}>
                {files.length} file{files.length === 1 ? '' : 's'} selected • {formatFileSize(totalSize)}
                <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
                  {files.slice(0, 6).map((f, i) => (
                    <li key={i} style={{ fontSize: '12px', color: '#777' }}>
                      {f.name} ({formatFileSize(f.size)})
                    </li>
                  ))}
                  {files.length > 6 && (
                    <li style={{ fontSize: '12px', color: '#777' }}>…and {files.length - 6} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {files.length > 0 && (
            <div className="bordered-box">
              <h6 className="container-header">⚙️ Output Options</h6>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <FormField label="Format">
                  <select
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value)}
                    style={{ padding: '8px', border: '1px solid #d1d1d1', borderRadius: '4px' }}
                  >
                    <option value="png">PNG (lossless, larger)</option>
                    <option value="jpeg">JPEG (smaller file)</option>
                  </select>
                </FormField>

                {outputFormat === 'jpeg' && (
                  <FormField
                    label="🎯 JPEG Quality"
                    description="Full resolution is always kept; this only sets JPEG compression"
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: '220px' }}>
                      <input
                        type="range"
                        min="40"
                        max="100"
                        value={quality}
                        onChange={(e) => setQuality(parseInt(e.target.value))}
                        style={{ flex: 1, accentColor: '#FF9900' }}
                      />
                      <span style={{ minWidth: '40px' }}>{quality}%</span>
                    </div>
                  </FormField>
                )}
              </div>
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#565959' }}>
                {outputFormat === 'png'
                  ? 'PNG keeps every pixel exactly (lossless). Best quality, larger files.'
                  : 'JPEG at 90%+ is visually identical to the original at a fraction of the size.'}
              </div>
            </div>
          )}

          {processing && (
            <div className="bordered-box">
              <SpaceBetween size="m" direction="vertical" alignItems="center">
                <Spinner size="large" />
                <Box variant="p" color="text-body-secondary">
                  {progress || 'Converting...'}
                </Box>
                {files.length > 1 && (
                  <Box variant="small" color="text-body-secondary">
                    {done} / {files.length} done
                  </Box>
                )}
              </SpaceBetween>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default HeicConverter;
